import React, { FC, useEffect, useState } from 'react'

import { CircularProgress, Dialog, DialogActions, DialogContent, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import { endOfDay, format, parseISO, startOfDay } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { CustomButton } from '../../../components/CustomButton'
import { CommonTableColumnsEnum } from '../../../components/Table/types'
import { showToast } from '../../../components/Toasts'
import { APP_PATHS } from '../../../routes/paths'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { SortDirection } from '../../../types'
import { setPeriod } from '../../../user'
import { isDateInRange, useSort } from '../../../utils'
import { useIsMobile } from '../../../utils/useIsMobile'
import {
  DailyStats,
  DailyStatsItem,
  fillDailyStatsData,
  setExpandedDate,
  updateColumnOrder,
} from '../../DailyStats'
import { DailyStatsCardBody } from '../../DailyStats/Card/DailyStatsCardBody'
import { DailyStatsCardHead } from '../../DailyStats/Card/DailyStatsCardHead'
import { DailyStatsService } from '../../DailyStats/DailyStatsService'
import { Trade, TradeAiAnalysis } from '../../TradingLog/types'

import { CalendarModalProps } from './type'

export const DailyStatsModal: FC<CalendarModalProps> = ({ selectedDate, setSelectedDate }) => {
  const { spacing, breakpoints } = useTheme()
  const isMobile = useIsMobile('md')
  const { localJournalingProgressData } = useAppSelector((state) => state.localJournalingProgress)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const { currentAccount, period } = useAppSelector(({ userAccounts }) => userAccounts)
  const [startDate, endDate] = period

  const [dailyStatsData, setDailyStatsData] = useState<null | DailyStats>(null)
  const [fetchedDayData, setFetchedDayData] = useState<DailyStatsItem>()

  const handleAnalysisSaved = (id: number, analysis: TradeAiAnalysis | null) => {
    setDailyStatsData((prev) => {
      if (prev) {
        return {
          ...prev,
          tradingLogTableData: prev.tradingLogTableData.map((trade) =>
            Number(trade.id) === id ? { ...trade, tradeAiAnalysis: analysis } : trade,
          ),
        }
      }

      return prev
    })
  }

  const formattedDate = selectedDate
    ? format(selectedDate, 'yyyy-MM-dd')
    : format(new Date(), 'yyyy-MM-dd')

  const {
    pagination: { columns },
  } = useAppSelector((state) => state.dailyStats)

  const handleClose = () => {
    setSelectedDate(null)
  }

  const redirectToDayStats = () => {
    if (
      selectedDate &&
      startDate &&
      endDate &&
      isDateInRange(selectedDate, parseISO(startDate), parseISO(endDate))
    ) {
      dispatch(setExpandedDate(selectedDate.toISOString()))
      navigate(APP_PATHS.dailyStats)
      return
    } else if (selectedDate) {
      dispatch(
        setPeriod([
          format(startOfDay(selectedDate), "yyyy-MM-dd'T'HH:mm:ss.SSS"),
          format(endOfDay(selectedDate), "yyyy-MM-dd'T'HH:mm:ss.SSS"),
        ]),
      )
      dispatch(setExpandedDate(selectedDate.toISOString()))
      dispatch(fillDailyStatsData(fetchedDayData))
      navigate(APP_PATHS.dailyStats, { state: { fromModal: true } })
      return
    }
  }

  const fetchDailyStatsData = async () => {
    try {
      if (selectedDate && currentAccount?.id) {
        const date = new Date(selectedDate)

        const year = date.getFullYear()
        const month = date.getMonth()
        const day = date.getDate()

        const start = new Date(Date.UTC(year, month, day)).toISOString()
        const end = new Date(Date.UTC(year, month, day, 23, 59, 59, 999)).toISOString()

        const { data: response } = await DailyStatsService.getTrades({
          page: 1,
          perPage: 100,
          columns: [CommonTableColumnsEnum.id, CommonTableColumnsEnum.symbol, ...columns],
          period: [start, end],
          account: currentAccount.id,
          fromCalendar: true,
        })

        const preparedData = Object.values(response.data)[0]
        setDailyStatsData(preparedData)
        setFetchedDayData(response.data)
      }
    } catch (error) {
      showToast.error(error.message)
    }
  }

  useEffect(() => {
    ;(async () => {
      await fetchDailyStatsData()
    })()
  }, [selectedDate, columns, localJournalingProgressData])

  const dispatchNewReorder = (data: (keyof Trade)[]) => {
    dispatch(updateColumnOrder(data))
  }

  const { sortingColumn, handleSort, sortingDirection, sortedTrades, handleColumnReorder } =
    useSort<Trade>(dailyStatsData?.tradingLogTableData || [], columns, dispatchNewReorder, {
      orderBy: CommonTableColumnsEnum.closeTime,
      direction: SortDirection.desc,
    })

  return (
    <Dialog
      open={!!selectedDate}
      onClose={handleClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          maxHeight: isMobile ? '100%' : '80vh',
          margin: isMobile ? 0 : spacing(2),
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogContent sx={{ p: spacing(2), overflowY: 'auto' }}>
        {dailyStatsData ? (
          <Box>
            <DailyStatsCardHead
              date={formattedDate}
              opened
              isForModal
              dailyStatsInfo={dailyStatsData.dailyStatsInfo}
              handleSetOpened={() => null}
            />

            <DailyStatsCardBody
              sortingProps={{
                handleSort,
                handleColumnReorder,
                sortingColumn,
                sortedTrades,
                sortingDirection,
              }}
              onAnalysisSaved={handleAnalysisSaved}
            />
          </Box>
        ) : (
          <Box height={spacing(55)} display="flex" alignItems="center" justifyContent="center">
            <CircularProgress color="inherit" />
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: 'flex-end',
          gap: spacing(2),
          [breakpoints.down('md')]: { marginTop: 0 },
          p: spacing(2),
        }}
      >
        <CustomButton onClick={handleClose} color={'secondary'}>
          {t('cancel', 'Cancel')}
        </CustomButton>
        <CustomButton onClick={redirectToDayStats} sx={{ minWidth: spacing(17) }}>
          {t('dailyStatsPage.modal.goToDayStats', 'Go to Day stats')}
        </CustomButton>
      </DialogActions>
    </Dialog>
  )
}
